/**
 * Navigation Guard Utilities
 *
 * Utilitários para prevenir problemas de navegação concorrente e loops de redirecionamento
 */

// useNavigationGuard usa useEffect sem importar o React.
import { useEffect } from 'react';

const NAVIGATION_STATE_KEY = 'fila_navigation_state';
const REDIRECT_DEBOUNCE_KEY = 'fila_redirect_debounce';

interface NavigationState {
  lastRedirect: number;
  currentRoute: string;
  isNavigating: boolean;
}

/**
 * Marca início de navegação para prevenir redirecionamentos concorrentes
 */
export function startNavigation(route: string): void {
  try {
    const state: NavigationState = {
      lastRedirect: Date.now(),
      currentRoute: route,
      isNavigating: true,
    };

    sessionStorage.setItem(NAVIGATION_STATE_KEY, JSON.stringify(state));
  } catch (error) {
    console.warn('Erro ao marcar início da navegação:', error);
  }
}

/**
 * Finaliza navegação atual
 */
export function endNavigation(): void {
  try {
    const stateStr = sessionStorage.getItem(NAVIGATION_STATE_KEY);
    if (stateStr) {
      const state = JSON.parse(stateStr) as NavigationState;
      state.isNavigating = false;
      sessionStorage.setItem(NAVIGATION_STATE_KEY, JSON.stringify(state));
    }
  } catch (error) {
    console.warn('Erro ao finalizar navegação:', error);
  }
}

/**
 * Verifica se pode redirecionar com base no estado atual
 */
export function canRedirect(debounceMs: number = 1000): boolean {
  try {
    const stateStr = sessionStorage.getItem(NAVIGATION_STATE_KEY);
    const debounceStr = sessionStorage.getItem(REDIRECT_DEBOUNCE_KEY);

    if (!stateStr) {
      return true;
    }

    const state = JSON.parse(stateStr) as NavigationState;
    const now = Date.now();

    // Se está navegando, verificar se já passou tempo suficiente
    if (state.isNavigating && (now - state.lastRedirect) < debounceMs) {
      console.log('🔄 Navegação em progresso, ignorando redirecionamento');
      return false;
    }

    // Verificar debounce de redirecionamento
    if (debounceStr) {
      const lastRedirect = parseInt(debounceStr);
      if (now - lastRedirect < debounceMs) {
        console.log('🔄 Redirecionamento muito recente, ignorando');
        return false;
      }
    }

    return true;
  } catch (error) {
    console.warn('Erro ao verificar redirecionamento:', error);
    return true; // Em caso de erro, permitir redirecionamento
  }
}

/**
 * Marca redirecionamento para debounce
 */
export function markRedirect(): void {
  try {
    sessionStorage.setItem(REDIRECT_DEBOUNCE_KEY, Date.now().toString());
  } catch (error) {
    console.warn('Erro ao marcar redirecionamento:', error);
  }
}

/**
 * Limpa estado de navegação (usado em logout ou reset)
 */
export function clearNavigationState(): void {
  try {
    sessionStorage.removeItem(NAVIGATION_STATE_KEY);
    sessionStorage.removeItem(REDIRECT_DEBOUNCE_KEY);
  } catch (error) {
    console.warn('Erro ao limpar estado de navegação:', error);
  }
}

/**
 * Obtém informações sobre estado atual de navegação
 */
export function getNavigationState(): NavigationState | null {
  try {
    const stateStr = sessionStorage.getItem(NAVIGATION_STATE_KEY);
    return stateStr ? JSON.parse(stateStr) as NavigationState : null;
  } catch (error) {
    console.warn('Erro ao obter estado de navegação:', error);
    return null;
  }
}

/**
 * Hook para proteger componentes contra reloads acidentais durante operações críticas
 */
export function useNavigationGuard(isOperating: boolean = false): void {
  if (typeof window === 'undefined') return; // SSR protection

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isOperating) {
        e.preventDefault();
        e.returnValue = 'Operação em andamento. Tem certeza que deseja sair?';
        return e.returnValue;
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [isOperating]);
}